/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Credit Consumption by Module Distribution Component (Task 5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  FileText,
  Image,
  Video,
  Mic,
  Languages,
  MessageSquare,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useCreditLedgerStore,
  LedgerCategory,
} from '../../stores/useCreditLedgerStore';
import { formatCreditNumber } from '../../hooks/useWalletBalance';

export interface CategoryMetric {
  key: LedgerCategory;
  nameKey: string;
  icon: React.ElementType;
  credits: number;
  percentage: number;
  operationCount: number;
  colorClass: string;
  badgeClass: string;
  ringClass: string;
}

export const CreditCategoryDistribution: React.FC = () => {
  const { t } = useTranslation();
  const { entries, categoryFilter, setCategoryFilter } = useCreditLedgerStore();

  // Aggregate debit consumption by module category
  const { categories, totalConsumed } = useMemo(() => {
    let totalDebit = 0;
    const catMap: Record<string, { credits: number; count: number }> = {
      text: { credits: 0, count: 0 },
      chat: { credits: 0, count: 0 },
      images: { credits: 0, count: 0 },
      video: { credits: 0, count: 0 },
      voice: { credits: 0, count: 0 },
      translation: { credits: 0, count: 0 },
    };

    entries.forEach((entry) => {
      if (entry.amount < 0) {
        const absAmount = Math.abs(entry.amount);
        totalDebit += absAmount;

        let targetCat = entry.category;
        if (targetCat === 'avatar') {
          targetCat = 'video';
        }

        if (catMap[targetCat]) {
          catMap[targetCat].credits += absAmount;
          catMap[targetCat].count += 1;
        }
      }
    });

    const catConfigs: Array<{
      key: LedgerCategory;
      nameKey: string;
      icon: React.ElementType;
      colorClass: string;
      badgeClass: string;
      ringClass: string;
    }> = [
      {
        key: 'text',
        nameKey: 'text',
        icon: FileText,
        colorClass: 'bg-primary',
        badgeClass: 'bg-primary/10 text-primary border-primary/20',
        ringClass: 'ring-primary/40',
      },
      {
        key: 'chat',
        nameKey: 'chat',
        icon: MessageSquare,
        colorClass: 'bg-emerald-500',
        badgeClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
        ringClass: 'ring-emerald-500/40',
      },
      {
        key: 'images',
        nameKey: 'images',
        icon: Image,
        colorClass: 'bg-accent',
        badgeClass: 'bg-accent/10 text-accent border-accent/20',
        ringClass: 'ring-accent/40',
      },
      {
        key: 'video',
        nameKey: 'video',
        icon: Video,
        colorClass: 'bg-rose-500',
        badgeClass: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
        ringClass: 'ring-rose-500/40',
      },
      {
        key: 'voice',
        nameKey: 'voice',
        icon: Mic,
        colorClass: 'bg-amber-500',
        badgeClass: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
        ringClass: 'ring-amber-500/40',
      },
      {
        key: 'translation',
        nameKey: 'translation',
        icon: Languages,
        colorClass: 'bg-cyan-500',
        badgeClass: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
        ringClass: 'ring-cyan-500/40',
      },
    ];

    const result: CategoryMetric[] = catConfigs.map((cfg) => {
      const stats = catMap[cfg.key] || { credits: 0, count: 0 };
      const pct = totalDebit > 0 ? Number(((stats.credits / totalDebit) * 100).toFixed(1)) : 0;
      return {
        key: cfg.key,
        nameKey: cfg.nameKey,
        icon: cfg.icon,
        credits: stats.credits,
        percentage: pct,
        operationCount: stats.count,
        colorClass: cfg.colorClass,
        badgeClass: cfg.badgeClass,
        ringClass: cfg.ringClass,
      };
    });

    return {
      categories: result,
      totalConsumed: Number(totalDebit.toFixed(2)),
    };
  }, [entries]);

  const handleCategoryToggle = (catKey: LedgerCategory) => {
    if (categoryFilter === catKey) {
      setCategoryFilter('all');
    } else {
      setCategoryFilter(catKey);
    }
  };

  return (
    <div className="p-5 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-4">
      {/* Header with Title and Total Consumed Badge */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="size-9 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
            <PieChart className="size-5" />
          </div>
          <div>
            <h2 className="text-sm font-bold font-heading text-foreground">
              {t('ledger.distribution.title')}
            </h2>
            <p className="text-xs text-foreground-muted">
              {t('ledger.distribution.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto px-3 py-1.5 rounded-xl bg-surface-elevated border border-surface-border text-xs">
          <span className="text-foreground-muted">{t('ledger.distribution.totalUsed')}:</span>
          <span className="font-mono font-bold text-foreground">
            {formatCreditNumber(totalConsumed)}
          </span>
          <span className="text-[10px] text-foreground-muted uppercase font-semibold">
            {t('ledger.stats.credits')}
          </span>
        </div>
      </div>

      {/* Multi-Segment Proportional Progress Bar */}
      <div className="space-y-1.5">
        <div className="w-full bg-muted/70 rounded-full h-3 overflow-hidden flex p-0.5 border border-surface-border/50">
          {totalConsumed > 0 ? (
            categories.map((cat) => {
              if (cat.percentage <= 0) return null;
              return (
                <div
                  key={cat.key}
                  style={{ width: `${cat.percentage}%` }}
                  className={`h-full ${cat.colorClass} first:rounded-l-full last:rounded-r-full transition-all duration-300 relative group`}
                  title={`${t(`ledger.distribution.categories.${cat.nameKey}`)}: ${cat.percentage}% (${formatCreditNumber(cat.credits)} credits)`}
                />
              );
            })
          ) : (
            <div className="w-full h-full bg-muted rounded-full" />
          )}
        </div>

        {/* Legend pills under progress bar */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-0.5 text-[11px] text-foreground-muted">
          {categories.map((cat) => (
            <div key={cat.key} className="flex items-center gap-1.5">
              <span className={`size-2 rounded-full ${cat.colorClass}`} />
              <span className="font-medium text-foreground">
                {t(`ledger.distribution.categories.${cat.nameKey}`)}
              </span>
              <span className="font-mono text-[10px] text-foreground-muted">
                ({cat.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Interactive 6-Studio Category Breakdown Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-1">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const isSelected = categoryFilter === cat.key;

          return (
            <button
              type="button"
              key={cat.key}
              onClick={() => handleCategoryToggle(cat.key)}
              className={`p-3 rounded-xl border text-left transition-all relative flex flex-col justify-between group focus:outline-none ${
                isSelected
                  ? `border-primary bg-primary/5 ring-2 ${cat.ringClass} shadow-xs`
                  : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated hover:border-surface-border/80'
              }`}
            >
              {isSelected && (
                <div className="absolute top-2 right-2 text-primary">
                  <CheckCircle2 className="size-3.5" />
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className={`size-7 rounded-lg flex items-center justify-center ${cat.badgeClass}`}>
                    <Icon className="size-3.5" />
                  </div>
                  {!isSelected && (
                    <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-muted text-foreground-muted">
                      {cat.percentage}%
                    </span>
                  )}
                </div>

                <div>
                  <div className="text-xs font-semibold text-foreground truncate">
                    {t(`ledger.distribution.categories.${cat.nameKey}`)}
                  </div>
                  <div className="font-mono text-sm font-bold text-foreground pt-0.5">
                    {formatCreditNumber(cat.credits)}
                  </div>
                </div>
              </div>

              <div className="text-[10px] text-foreground-muted pt-2 border-t border-surface-border/40 mt-2 truncate">
                {t('ledger.distribution.operations', { count: cat.operationCount })}
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer Edge Engine Latency Tip */}
      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-surface-elevated border border-surface-border text-[11px] text-foreground-muted">
        <Sparkles className="size-3.5 text-primary flex-shrink-0" />
        <span className="leading-relaxed">
          {t('ledger.distribution.tip')}
        </span>
      </div>
    </div>
  );
};
